import { useState } from 'react';
import type { FinanceOrderPage, FinanceOrderSummary } from '@gatecheckpro/contracts';
import { Button, Card, EmptyState, LoadingRegion, formatMoney } from '@gatecheckpro/design-system';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';

/**
 * Finding the order somebody is calling about.
 *
 * The two things an operator actually holds are an order number read off a
 * confirmation and the email address the customer is phoning from. Neither is a
 * fragment, which is why the server matches exactly and this form does not
 * pretend otherwise — a box promising to search would invite half an email
 * address and return nothing, and the operator would conclude the order does
 * not exist.
 *
 * What replaced this was worse: until the discovery read landed, this surface
 * asked for a refund's primary key. An operator with a customer on the phone
 * does not have one, and a workflow that begins by requiring it is not a
 * workflow. That temporary box and the notice explaining its absence are gone
 * rather than adapted, because they existed only to be honest about a gap.
 */
export function OrderSearch({
  base,
  onOpen,
}: {
  base: string;
  onOpen: (order: FinanceOrderSummary) => void;
}): React.JSX.Element {
  const [orderNumber, setOrderNumber] = useState('');
  const [purchaserEmail, setPurchaserEmail] = useState('');
  const [query, setQuery] = useState<string | null>(null);

  const results = useResource<FinanceOrderPage>(
    query === null ? null : `${base}/finance/orders?${query}`,
  );

  return (
    <Card title="Find an order" eyebrow="By order number or the address it was placed under">
      <div className="gc-stack">
        <form
          className="gc-stack gc-stack--tight"
          onSubmit={(formEvent) => {
            formEvent.preventDefault();
            const search = new URLSearchParams();
            if (orderNumber.trim() !== '') search.set('orderNumber', orderNumber.trim());
            if (purchaserEmail.trim() !== '') search.set('purchaserEmail', purchaserEmail.trim());
            // Nothing to ask for. Sending an empty search would page through the
            // tenant's whole order history, which is not what anybody meant.
            setQuery(search.toString() === '' ? null : search.toString());
          }}
        >
          <label className="gc-field">
            <span className="gc-field__label">Order number</span>
            <input
              className="gc-input"
              value={orderNumber}
              onChange={(changeEvent) => setOrderNumber(changeEvent.target.value)}
            />
          </label>
          <label className="gc-field">
            <span className="gc-field__label">Purchaser email</span>
            <input
              className="gc-input"
              /*
               * Text, not `type="email"`.
               *
               * Native validation refuses to submit anything that is not a
               * well-formed address, so a partial one — the exact case an
               * operator hits when they mistype what a customer read out — was
               * silently swallowed: no request, no result, and a spinner that
               * said "Searching". That is a client deciding eligibility, which
               * is the thing this lane must not do. The server matches exactly
               * and says nothing was found, which is the honest answer.
               */
              inputMode="email"
              value={purchaserEmail}
              onChange={(changeEvent) => setPurchaserEmail(changeEvent.target.value)}
            />
          </label>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        {query !== null && results.resource.status === 'loading' && (
          <LoadingRegion label="Searching" />
        )}
        {results.resource.status === 'failed' && <FailureNotice error={results.resource.error} />}
        {results.resource.status === 'loaded' &&
          (results.resource.data.orders.length === 0 ? (
            <EmptyState title="No order matches that">
              Order numbers and email addresses are matched exactly, so a partial one finds nothing.
              Check the number on the customer&rsquo;s confirmation.
            </EmptyState>
          ) : (
            <div className="gc-stack gc-stack--tight">
              {results.resource.data.orders.map((order) => (
                <button
                  key={order.orderId}
                  type="button"
                  className="gc-card gc-card--button"
                  data-testid={`order-result-${order.orderNumber}`}
                  onClick={() => onOpen(order)}
                >
                  <span className="gc-row gc-row--between gc-row--baseline">
                    <strong>{order.orderNumber}</strong>
                    <span className="gc-mono">{formatMoney(order.amount)}</span>
                  </span>
                  <span className="gc-muted">
                    {order.eventTitle} · {order.purchaserEmail}
                  </span>
                  {/*
                    The server's figure, printed. What is still refundable
                    depends on the over-refund policy the command enforces, and
                    a surface that recomputed it would eventually offer a refund
                    the next call refuses — which is worse than offering none.
                  */}
                  <span className="gc-muted">
                    {formatMoney({
                      minor: order.refundableMinor,
                      currency: order.amount.currency,
                    })}{' '}
                    still refundable
                    {order.alreadyClaimedMinor > 0 && (
                      <>
                        {' · '}
                        {formatMoney({
                          minor: order.alreadyClaimedMinor,
                          currency: order.amount.currency,
                        })}{' '}
                        already claimed
                      </>
                    )}
                  </span>
                </button>
              ))}
            </div>
          ))}
      </div>
    </Card>
  );
}
